import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import { CheckIcon } from 'lucide-react'
import { useId, type ComponentProps, type ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { CONTROL } from './control'

/**
 * Campos de formulario del sistema (C8): etiqueta arriba, control, ayuda o
 * error debajo. Un solo aspecto para inputs, áreas de texto y selects.
 */

interface CampoProps {
  etiqueta: string
  /** Texto chico debajo del control ("Se usa en las vistas previas."). */
  ayuda?: ReactNode
  error?: string | undefined
  /** Contenido a la derecha de la ayuda, p. ej. el contador 38 / 140. */
  extremo?: ReactNode
  className?: string
  children: (props: { id: string; describedBy?: string; invalido: boolean }) => ReactNode
}

export function Campo({
  etiqueta,
  ayuda,
  error,
  extremo,
  className,
  children,
}: CampoProps) {
  const id = useId()
  const idAyuda = `${id}-ayuda`
  const hayPie = Boolean(ayuda ?? error ?? extremo)
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className="text-sm font-medium">
        {etiqueta}
      </label>
      {children({
        id,
        ...(hayPie ? { describedBy: idAyuda } : {}),
        invalido: Boolean(error),
      })}
      {hayPie && (
        <div
          id={idAyuda}
          className={cn(
            'flex items-start justify-between gap-3 text-xs',
            error ? 'text-nuevo' : 'text-tinta-suave',
          )}
        >
          <span role={error ? 'alert' : undefined}>{error ?? ayuda}</span>
          {extremo && <span className="shrink-0 tabular-nums">{extremo}</span>}
        </div>
      )}
    </div>
  )
}

export function Entrada({ className, ...props }: ComponentProps<'input'>) {
  return <input className={cn(CONTROL, className)} {...props} />
}

/** Input con un prefijo fijo a la izquierda, como el @ de Instagram. */
export function EntradaConPrefijo({
  prefijo,
  className,
  ...props
}: ComponentProps<'input'> & { prefijo: string }) {
  return (
    <div
      className={cn(
        'flex overflow-hidden rounded-xl border border-borde bg-tarjeta',
        className,
      )}
    >
      <span className="flex items-center border-r border-borde bg-lienzo px-3.5 text-sm text-tinta-suave">
        {prefijo}
      </span>
      <input
        className="min-w-0 flex-1 bg-transparent px-3.5 py-2.5 text-base outline-none placeholder:text-tinta-suave/70 sm:text-sm"
        {...props}
      />
    </div>
  )
}

export function AreaTexto({ className, ...props }: ComponentProps<'textarea'>) {
  return <textarea className={cn(CONTROL, 'min-h-24 resize-y', className)} {...props} />
}

export function Selector({ className, children, ...props }: ComponentProps<'select'>) {
  return (
    <select
      className={cn(
        CONTROL,
        // La flecha: con appearance-none el navegador no la dibuja.
        "appearance-none bg-[url('data:image/svg+xml;utf8,<svg%20xmlns=%22http://www.w3.org/2000/svg%22%20viewBox=%220%200%2024%2024%22%20fill=%22none%22%20stroke=%22%236b7280%22%20stroke-width=%222%22><path%20d=%22m6%209%206%206%206-6%22/></svg>')] bg-[length:1.1rem] bg-[right_0.9rem_center] bg-no-repeat pr-10",
        className,
      )}
      {...props}
    >
      {children}
    </select>
  )
}

interface CasillaProps extends Omit<
  ComponentProps<typeof CheckboxPrimitive.Root>,
  'children'
> {
  etiqueta: ReactNode
  detalle?: ReactNode
}

/** Casilla de verificación con área táctil de 44 px (§2.8). */
export function Casilla({ etiqueta, detalle, className, ...props }: CasillaProps) {
  const id = useId()
  return (
    <div className={cn('flex min-h-11 items-center gap-3', className)}>
      <CheckboxPrimitive.Root
        id={id}
        className={cn(
          'flex size-6 shrink-0 items-center justify-center rounded-md border border-borde bg-tarjeta',
          'data-[state=checked]:border-tinta data-[state=checked]:bg-tinta data-[state=checked]:text-white',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-tinta',
          'disabled:opacity-40',
        )}
        {...props}
      >
        <CheckboxPrimitive.Indicator>
          <CheckIcon className="size-4" strokeWidth={3} aria-hidden />
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
      <label htmlFor={id} className="flex-1 cursor-pointer text-sm select-none">
        {etiqueta}
        {detalle && <span className="ml-1.5 text-tinta-suave">{detalle}</span>}
      </label>
    </div>
  )
}
